import { useEffect } from "react";
import { useLocation } from "wouter";

const MAX_TARGET_ATTEMPTS = 4;
const HASH_REALIGN_DELAYS_MS = [180, 600, 1_500];

function scrollToPageTop() {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}

function getFragmentTarget(hash: string) {
  if (!hash) return null;

  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return document.getElementById(hash.slice(1));
  }
}

function scrollTargetToVisibleStart(target: HTMLElement) {
  const scrollMarginTop = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
  const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - scrollMarginTop);
  window.scrollTo({ top, left: 0, behavior: "instant" });
}

export function scrollToNavigationDestination(hash = window.location.hash) {
  let attempts = 0;

  const scroll = () => {
    if (!hash) {
      scrollToPageTop();
      return;
    }

    const target = getFragmentTarget(hash);
    if (target) {
      scrollTargetToVisibleStart(target);
      return;
    }

    attempts += 1;
    if (attempts < MAX_TARGET_ATTEMPTS) {
      window.requestAnimationFrame(scroll);
      return;
    }

    scrollToPageTop();
  };

  window.requestAnimationFrame(scroll);
}

export function NavigationScrollManager() {
  const [location] = useLocation();

  useEffect(() => {
    const hash = window.location.hash;
    scrollToNavigationDestination(hash);

    if (!hash) return;

    let cancelled = false;
    const realign = () => {
      if (!cancelled && window.location.hash === hash) scrollToNavigationDestination(hash);
    };
    const timeoutIds = HASH_REALIGN_DELAYS_MS.map((delay) => window.setTimeout(realign, delay));

    void document.fonts?.ready.then(realign);

    return () => {
      cancelled = true;
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [location]);

  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    const handleHashChange = () => scrollToNavigationDestination();
    const handleInternalLinkClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const origin = event.target as Element | null;
      const link = origin?.closest<HTMLAnchorElement>("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;

      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin) return;

      const isCurrentDocument = destination.pathname === window.location.pathname && destination.search === window.location.search;
      if (isCurrentDocument) scrollToNavigationDestination(destination.hash);
    };

    window.addEventListener("hashchange", handleHashChange);
    document.addEventListener("click", handleInternalLinkClick, true);

    return () => {
      window.history.scrollRestoration = previousRestoration;
      window.removeEventListener("hashchange", handleHashChange);
      document.removeEventListener("click", handleInternalLinkClick, true);
    };
  }, []);

  return null;
}
